import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { createShare, deleteShare } from '../../services/shareApi';

const EXPIRATION_OPTIONS = [
  { value: 0, label: 'Never expires' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

/**
 * ShareModal - Modal for creating and managing share links
 */
export const ShareModal = ({ isOpen, onClose, projectData, projectName = 'Floor Plan' }) => {
  const [step, setStep] = useState('create'); // 'create' or 'success'
  const [title, setTitle] = useState(projectName);
  const [expiresInDays, setExpiresInDays] = useState(0);
  const [allowComments, setAllowComments] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shareResult, setShareResult] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCreateShare = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await createShare(projectData, {
        title,
        expiresInDays,
        allowComments,
      });

      setShareResult(result);
      setStep('success');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (shareResult?.shareUrl) {
      try {
        await navigator.clipboard.writeText(shareResult.shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareResult.shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleOpenPreview = () => {
    if (shareResult?.shareUrl) {
      window.open(shareResult.shareUrl, '_blank');
    }
  };

  const handleShareVia = async (method) => {
    if (!shareResult?.shareUrl) return;

    const url = shareResult.shareUrl;
    const text = `Check out this floor plan: ${title}`;
    const fullText = `${text}\n\n${url}`;

    // Try Web Share API first (only on HTTPS - required for Web Share API)
    const isSecureContext = window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    if (isSecureContext && navigator.share && (method === 'email' || method === 'sms')) {
      try {
        await navigator.share({
          title: title,
          text: fullText,
          url: url,
        });
        return;
      } catch (err) {
        // User cancelled or not supported, fall through to traditional method
        if (err.name === 'AbortError') return;
      }
    }

    // Traditional share methods
    switch (method) {
      case 'email': {
        const mailtoUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + '\n\n' + url)}`;
        window.open(mailtoUrl, '_blank');
        break;
      }
      case 'sms': {
        const smsUrl = `sms:?body=${encodeURIComponent(text + ' ' + url)}`;
        window.open(smsUrl, '_blank');
        break;
      }
      case 'whatsapp': {
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
        break;
      }
    }
  };

  const handleDeleteShare = async () => {
    if (!shareResult?.shareId) return;

    if (!confirm('Are you sure you want to delete this share link?')) return;

    setIsLoading(true);
    try {
      await deleteShare(shareResult.shareId);
      setStep('create');
      setShareResult(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep('create');
    setShareResult(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            {step === 'create' ? 'Share Floor Plan' : 'Share Link Created'}
          </h2>
          <Button variant="ghost" size="small" onClick={handleClose} style={{ padding: '4px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          {step === 'create' ? (
            <>
              {/* Project Title */}
              <div style={styles.field}>
                <label style={styles.label}>Project Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  style={styles.input}
                  placeholder="Enter project title..."
                />
              </div>

              {/* Expiration */}
              <div style={styles.field}>
                <label style={styles.label}>Link Expiration</label>
                <select
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                  style={styles.select}
                >
                  {EXPIRATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Allow Comments */}
              <div style={styles.checkboxField}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={allowComments}
                    onChange={(e) => setAllowComments(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span>Allow client to add comments</span>
                </label>
                <p style={styles.hint}>
                  Clients can pin comments directly on the floor plan
                </p>
              </div>

              {/* Create Button */}
              <Button
                variant="primary"
                onClick={handleCreateShare}
                disabled={isLoading || !title.trim()}
                style={{ width: '100%', padding: '12px' }}
              >
                {isLoading ? 'Creating...' : 'Create Share Link'}
              </Button>
            </>
          ) : (
            <>
              {/* Success State */}
              <div style={styles.successIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#00c8ff" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>

              {/* Share URL */}
              <div style={styles.field}>
                <label style={styles.label}>Share URL</label>
                <div style={styles.urlContainer}>
                  <input
                    type="text"
                    value={shareResult?.shareUrl || ''}
                    readOnly
                    style={styles.urlInput}
                  />
                  <Button
                    variant="primary"
                    onClick={handleCopyLink}
                    style={{
                      padding: '10px 16px',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      background: copied ? '#00ff88' : undefined,
                    }}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={styles.actionButtons}>
                <Button
                  variant="default"
                  onClick={handleOpenPreview}
                  style={{ flex: 1, padding: '10px 16px', fontSize: '13px', gap: '6px' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                    <path d="M15 3h6v6" />
                    <path d="M10 14L21 3" />
                  </svg>
                  Open Preview
                </Button>
              </div>

              {/* Share Via */}
              <div style={styles.shareVia}>
                <label style={styles.label}>Share via</label>
                <div style={styles.shareButtons}>
                  <Button
                    variant="default"
                    onClick={() => handleShareVia('email')}
                    style={{ flex: 1, padding: '10px', flexDirection: 'column', gap: '4px', fontSize: '12px' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <path d="M22 6l-10 7L2 6" />
                    </svg>
                    Email
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => handleShareVia('sms')}
                    style={{ flex: 1, padding: '10px', flexDirection: 'column', gap: '4px', fontSize: '12px' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                    SMS
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => handleShareVia('whatsapp')}
                    style={{ flex: 1, padding: '10px', flexDirection: 'column', gap: '4px', fontSize: '12px' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    WhatsApp
                  </Button>
                </div>
              </div>

              {/* Delete Link */}
              <Button
                variant="danger"
                onClick={handleDeleteShare}
                disabled={isLoading}
                style={{ width: '100%', padding: '10px', marginTop: '16px' }}
              >
                {isLoading ? 'Deleting...' : 'Delete Share Link'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#1a2332',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '420px',
    maxHeight: '90vh',
    overflow: 'auto',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#6080a0',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '20px',
  },
  error: {
    backgroundColor: 'rgba(255, 100, 100, 0.1)',
    border: '1px solid rgba(255, 100, 100, 0.3)',
    color: '#ff6464',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    color: '#6080a0',
    marginBottom: '6px',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0d1117',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer',
  },
  checkboxField: {
    marginBottom: '20px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  hint: {
    margin: '4px 0 0 24px',
    fontSize: '11px',
    color: '#6080a0',
  },
  primaryButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#00c8ff',
    border: 'none',
    borderRadius: '6px',
    color: '#000',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  secondaryButton: {
    flex: 1,
    padding: '10px 16px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  dangerButton: {
    width: '100%',
    padding: '10px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255, 100, 100, 0.3)',
    borderRadius: '6px',
    color: '#ff6464',
    fontSize: '13px',
    cursor: 'pointer',
    marginTop: '16px',
  },
  successIcon: {
    textAlign: 'center',
    marginBottom: '16px',
  },
  urlContainer: {
    display: 'flex',
    gap: '8px',
  },
  urlInput: {
    flex: 1,
    padding: '10px 12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '12px',
    outline: 'none',
  },
  copyButton: {
    padding: '10px 16px',
    backgroundColor: '#00c8ff',
    border: 'none',
    borderRadius: '6px',
    color: '#000',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  copiedButton: {
    padding: '10px 16px',
    backgroundColor: '#00ff88',
    border: 'none',
    borderRadius: '6px',
    color: '#000',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  shareVia: {
    marginBottom: '8px',
  },
  shareButtons: {
    display: 'flex',
    gap: '8px',
  },
  shareButton: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
};

export default ShareModal;
